import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";

export interface FactoryProduct {
  id: string;
  name: string;
  samplePrice: number;
  bulkPrice: number;
  moq: number;
  image: string;
  category: string;
}

export interface Factory {
  id: string;
  name: string;
  city: string;
  established: number;
  employees: string;
  verified: boolean;
  dropshipping: boolean;
  warehouseCountries?: string[];
  rating: number;
  responseRate: string;
  onTimeDelivery: string;
  productCount: number;
  categories: string[];
  description: string;
  logo: string;
  coverImage: string;
  products: FactoryProduct[];
}

const ProductOrderPanel = ({ product, factoryName }: { product: FactoryProduct; factoryName: string }) => {
  const [orderType, setOrderType] = useState<"sample" | "bulk" | null>(null);
  const [qty, setQty] = useState(1);
  const [submitted, setSubmitted] = useState(false);

  const minQty = orderType === "bulk" ? product.moq : 1;
  const unitPrice = orderType === "bulk" ? product.bulkPrice : product.samplePrice;
  const total = (unitPrice * qty).toFixed(2);

  if (submitted) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
        <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-2">
          <i className="ri-checkbox-circle-fill text-xl text-amber-500"></i>
        </div>
        <p className="font-bold text-gray-900 text-sm mb-1">Order Request Sent!</p>
        <p className="text-gray-500 text-xs">{qty} units of <strong>{product.name}</strong> from {factoryName}</p>
        <button onClick={() => { setSubmitted(false); setOrderType(null); setQty(1); }} className="mt-3 text-amber-600 text-xs font-semibold hover:underline cursor-pointer">
          Place another order
        </button>
      </div>
    );
  }

  return (
    <div className="border border-amber-100 rounded-xl p-4 bg-white">
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          onClick={() => { setOrderType("sample"); setQty(1); }}
          className={`p-2.5 rounded-lg border-2 text-left transition-all cursor-pointer ${orderType === "sample" ? "border-amber-500 bg-amber-50" : "border-gray-100 hover:border-amber-200"}`}
        >
          <div className="flex items-center gap-1.5 mb-1">
            <i className="ri-test-tube-line text-amber-500 text-sm"></i>
            <span className="text-xs font-bold text-gray-800">Sample</span>
          </div>
          <div className="text-amber-600 font-bold text-sm">${product.samplePrice}<span className="text-gray-400 text-xs font-normal">/unit</span></div>
          <div className="text-gray-400 text-xs">Min. 1 unit</div>
        </button>
        <button
          onClick={() => { setOrderType("bulk"); setQty(product.moq); }}
          className={`p-2.5 rounded-lg border-2 text-left transition-all cursor-pointer ${orderType === "bulk" ? "border-emerald-500 bg-emerald-50" : "border-gray-100 hover:border-emerald-200"}`}
        >
          <div className="flex items-center gap-1.5 mb-1">
            <i className="ri-stack-line text-emerald-600 text-sm"></i>
            <span className="text-xs font-bold text-gray-800">Bulk</span>
          </div>
          <div className="text-emerald-600 font-bold text-sm">${product.bulkPrice}<span className="text-gray-400 text-xs font-normal">/unit</span></div>
          <div className="text-gray-400 text-xs">Min. {product.moq} units</div>
        </button>
      </div>
      {orderType && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <button onClick={() => setQty((q) => Math.max(minQty, q - (orderType === "bulk" ? product.moq : 1)))} className="w-7 h-7 flex items-center justify-center border border-amber-200 rounded-lg text-amber-600 hover:bg-amber-50 cursor-pointer text-sm font-bold">
              <i className="ri-subtract-line"></i>
            </button>
            <input type="number" value={qty} min={minQty} onChange={(e) => setQty(Math.max(minQty, Number(e.target.value)))} className="flex-1 border border-amber-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:border-amber-400" />
            <button onClick={() => setQty((q) => q + (orderType === "bulk" ? product.moq : 1))} className="w-7 h-7 flex items-center justify-center border border-amber-200 rounded-lg text-amber-600 hover:bg-amber-50 cursor-pointer text-sm font-bold">
              <i className="ri-add-line"></i>
            </button>
          </div>
          <div className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-2 mb-3">
            <span className="text-xs text-gray-500">Est. Total</span>
            <span className="font-bold text-amber-600">${total}</span>
          </div>
          <button
            onClick={() => setSubmitted(true)}
            className={`w-full font-bold py-2.5 rounded-lg text-white text-xs transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 ${orderType === "sample" ? "bg-amber-500 hover:bg-amber-600" : "bg-emerald-600 hover:bg-emerald-700"}`}
          >
            <i className={orderType === "sample" ? "ri-test-tube-line" : "ri-stack-line"}></i>
            {orderType === "sample" ? "Request Sample" : "Place Bulk Order"}
          </button>
        </>
      )}
      {!orderType && <p className="text-center text-gray-400 text-xs">Select order type above</p>}
    </div>
  );
};

const FactoryCard = ({ factory }: { factory: Factory }) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-amber-100 hover:border-amber-300 transition-all duration-300">
      <div className="relative h-44 overflow-hidden">
        <img src={factory.coverImage} alt={factory.name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
          {factory.verified && (
            <span className="bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
              <i className="ri-shield-check-fill"></i> Verified Factory
            </span>
          )}
          {factory.dropshipping && (
            <span className="bg-emerald-500 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
              <i className="ri-truck-fill"></i> Dropshipping Available
            </span>
          )}
        </div>
        <div className="absolute bottom-3 left-4 right-4">
          <h3 className="font-bold text-white text-base leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>{factory.name}</h3>
          <p className="text-amber-300 text-xs mt-0.5">{factory.city} · Est. {factory.established} · {factory.employees} employees</p>
        </div>
      </div>

      <div className="px-5 py-4 border-b border-amber-50">
        <div className="flex items-start gap-3">
          <img src={factory.logo} alt={factory.name} className="w-12 h-12 rounded-xl object-cover border border-amber-100 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-gray-600 text-xs leading-relaxed line-clamp-2">{factory.description}</p>
            <div className="flex flex-wrap gap-1 mt-2">
              {factory.categories.map((c) => (
                <span key={c} className="bg-amber-50 border border-amber-200 text-amber-700 text-xs px-2 py-0.5 rounded-full">{c}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="text-center">
            <div className="flex items-center justify-center gap-0.5 mb-0.5">
              <i className="ri-star-fill text-amber-400 text-xs"></i>
              <span className="font-bold text-gray-900 text-sm">{factory.rating}</span>
            </div>
            <div className="text-gray-400 text-xs">Rating</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-gray-900 text-sm">{factory.responseRate}</div>
            <div className="text-gray-400 text-xs">Response</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-gray-900 text-sm">{factory.onTimeDelivery}</div>
            <div className="text-gray-400 text-xs">On-Time</div>
          </div>
        </div>
        {factory.dropshipping && factory.warehouseCountries && (
          <div className="mt-3 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
            <div className="flex items-center gap-1.5 mb-1">
              <i className="ri-global-line text-emerald-600 text-sm"></i>
              <span className="text-xs font-bold text-emerald-700">Ships from warehouses in:</span>
            </div>
            <p className="text-emerald-600 text-xs">{factory.warehouseCountries.join(" · ")}</p>
          </div>
        )}
      </div>

      <div className="px-5 py-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold text-gray-900 text-sm flex items-center gap-1.5">
            <i className="ri-price-tag-3-line text-amber-500"></i>
            Products &amp; Pricing ({factory.products.length})
          </h4>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-amber-600 text-xs font-semibold flex items-center gap-1 cursor-pointer hover:text-amber-700 whitespace-nowrap"
          >
            {expanded ? "Hide" : "Show All"}
            <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
          </button>
        </div>
        <div className="space-y-5">
          {factory.products.slice(0, expanded ? factory.products.length : 2).map((product) => (
            <div key={product.id} className="border border-amber-50 rounded-xl overflow-hidden">
              <div className="flex gap-3 p-3 bg-amber-50/50">
                <Link to={`/factory-product/${factory.id}/${product.id}`} className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border border-amber-100 cursor-pointer hover:opacity-90 transition-opacity">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/factory-product/${factory.id}/${product.id}`} className="font-semibold text-gray-900 text-sm leading-tight mb-1 hover:text-amber-600 transition-colors cursor-pointer block">
                    {product.name}
                  </Link>
                  <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">{product.category}</span>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500 flex-wrap">
                    <span><i className="ri-test-tube-line text-amber-500 mr-0.5"></i>Sample: <strong className="text-gray-800">${product.samplePrice}</strong></span>
                    <span><i className="ri-stack-line text-emerald-500 mr-0.5"></i>Bulk: <strong className="text-gray-800">${product.bulkPrice}</strong></span>
                    <span>MOQ: <strong className="text-gray-800">{product.moq}</strong></span>
                  </div>
                </div>
              </div>
              <div className="p-3">
                <ProductOrderPanel product={product} factoryName={factory.name} />
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={() => navigate("/#rfq")}
          className="w-full mt-4 border border-amber-300 text-amber-700 hover:bg-amber-50 text-sm font-semibold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
        >
          <i className="ri-message-3-line"></i> Send RFQ to This Factory
        </button>
      </div>
    </div>
  );
};

export default FactoryCard;
